import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";
import { CheckCircle2, XCircle, RefreshCw } from "lucide-react";

export default function ComplianceMonitoring({ isAdmin }) {
  const { toast } = useToast();
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningId, setRunningId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/compliance/monitors");
      setMonitors(data.monitors || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (m) => {
    setRunningId(m.id);
    try {
      const { data } = await api.post(`/compliance/monitors/${m.id}/run`);
      setMonitors((prev) => prev.map((x) => (x.id === m.id ? data : x)));
      toast({ title: "Check re-run", description: m.name });
    } catch (e) {
      toast({ title: "Couldn't run check", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setRunningId(null); }
  };

  if (loading) return <div className="h-64 rounded-xl bg-muted/40 animate-pulse" />;

  const passing = monitors.filter((m) => m.last_result === "pass").length;

  return (
    <div className="space-y-3" data-testid="compliance-monitoring-page">
      <div className="text-[12.5px] text-muted-foreground">{passing} / {monitors.length} checks passing</div>
      <div className="rounded-xl border border-border bg-card divide-y divide-border">
        {monitors.map((m) => (
          <div key={m.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`compliance-monitor-row-${m.id}`}>
            <div className="flex items-center gap-2.5 min-w-0">
              {m.last_result === "pass" ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
              <div className="min-w-0">
                <div className="text-[12.5px] font-medium truncate">{m.name}</div>
                <div className="text-[11px] text-muted-foreground">{m.source} · {m.cadence}</div>
              </div>
            </div>
            {isAdmin && (
              <button
                type="button"
                onClick={() => run(m)}
                disabled={runningId === m.id}
                data-testid={`compliance-monitor-run-${m.id}`}
                className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-primary hover:underline shrink-0 disabled:opacity-50"
              >
                <RefreshCw className={cn("w-3.5 h-3.5", runningId === m.id && "animate-spin")} /> Re-run
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
